import React, { useState } from 'react';
import { FaShoppingCart, FaChartLine, FaTruck, FaCog } from 'react-icons/fa';
import Badge from '@/components/common/Badge';
import Button from '@/components/common/Button';
import Card, { CardBody } from '@/components/common/Card';
import RepuestosExternosInventario from '../RepuestosExternosInventario';
import CarritoRepuestosExternos from '../CarritoRepuestosExternos';
import PagoRepuestosExternos from '../../pagos/PagoRepuestosExternos';
import SeguimientoOrdenesExternas from '../../ordenes/SeguimientoOrdenesExternas';
import ComisionesProveedorDashboard from '../../comisiones/ComisionesProveedorDashboard';
import ConfiguracionTeknoParts from '../../proveedores/ConfiguracionTeknoParts';
import toastService from '@/utils/toastService';

/**
 * Componente principal que integra todo el sistema de repuestos con teknoParts
 * Permite navegar entre las diferentes secciones del marketplace
 */
const RepuestosMarketplace = () => {
  const [activeTab, setActiveTab] = useState('inventario');
  const [carrito, setCarrito] = useState([]);
  const [showCarrito, setShowCarrito] = useState(false);
  const [showPago, setShowPago] = useState(false);
  const [datosCarrito, setDatosCarrito] = useState(null);

  // Pestañas disponibles
  const tabs = [
    {
      id: 'inventario',
      label: 'Inventario',
      icon: FaShoppingCart,
      description: 'Catálogo exclusivo de repuestos teknoParts'
    },
    {
      id: 'ordenes',
      label: 'Órdenes',
      icon: FaTruck,
      description: 'Seguimiento de pedidos teknoParts'
    },
    {
      id: 'comisiones',
      label: 'Comisiones',
      icon: FaChartLine,
      description: 'Dashboard financiero y reportes'
    },
    {
      id: 'teknoParts',
      label: 'teknoParts',
      icon: FaCog,
      description: 'Configuración del proveedor exclusivo'
    }
  ];

  const handleAgregarAlCarrito = (repuesto) => {
    const itemExistente = carrito.find(item => item.id === repuesto.id);
    
    if (itemExistente) {
      // Incrementar cantidad si ya existe
      setCarrito(prev => prev.map(item =>
        item.id === repuesto.id
          ? { ...item, cantidad: item.cantidad + 1 }
          : item
      ));
    } else {
      // Agregar nuevo item
      setCarrito(prev => [...prev, { ...repuesto, cantidad: 1 }]);
    }
    
    toastService.success(`${repuesto.nombre} agregado al carrito`);
  };

  const handleUpdateCarrito = (nuevoCarrito) => {
    setCarrito(nuevoCarrito);
  };

  const handleProcederPago = (datos) => {
    setDatosCarrito(datos);
    setShowCarrito(false);
    setShowPago(true);
  };

  const handlePagoCompletado = (ordenCreada) => {
    // Limpiar carrito después del pago exitoso
    setCarrito([]);
    setShowPago(false);
    toastService.success('¡Pedido realizado exitosamente!');
    
    // Opcional: cambiar a la pestaña de órdenes para ver el seguimiento
    setActiveTab('ordenes');
  };

  const calcularTotalesCarrito = () => {
    const total = carrito.reduce((acc, item) => acc + (item.precio * item.cantidad), 0);
    const itemsExternos = carrito.filter(item => item.esExterno).length;
    const itemsPropios = carrito.filter(item => !item.esExterno).length;
    
    return {
      total,
      cantidadTotal: carrito.length,
      itemsExternos,
      itemsPropios
    };
  };

  const totalesCarrito = calcularTotalesCarrito();

  const renderActiveComponent = () => {
    switch (activeTab) {
      case 'inventario':
        return (
          <RepuestosExternosInventario
            onAgregarAlCarrito={handleAgregarAlCarrito}
            carrito={carrito}
          />
        );
      case 'ordenes':
        return <SeguimientoOrdenesExternas />;
      case 'comisiones':
        return <ComisionesProveedorDashboard />;
      case 'teknoParts':
        return <ConfiguracionTeknoParts />;
      default:
        return <div>Componente no encontrado</div>;
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <div className="p-4 md:p-6">
        {/* Header principal */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <div className="mb-4 md:mb-0">
            <h1 className="text-3xl font-bold text-white mb-2">
              Marketplace de Repuestos
            </h1>
            <p className="text-gray-400">
              Explora y compra repuestos premium de teknoParts con descuentos exclusivos
            </p>
          </div>
          
          {/* Botones del carrito - móvil: 50% cada uno */}
          <div className="flex items-center gap-2 md:gap-4 w-full md:w-auto">
            <div className="relative flex-1 md:flex-none">
              <Button
                variant="primary"
                onClick={() => setShowCarrito(true)}
                icon={<FaShoppingCart />}
                className="w-full md:w-auto text-sm md:text-base py-3 md:py-2"
              >
                Carrito
              </Button>
              {carrito.length > 0 && (
                <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center">
                  {carrito.length}
                </div>
              )}
            </div>
            
            {/* Info rápida del carrito */}
            {carrito.length > 0 && (
              <Card className="bg-blue-900/20 border-blue-800/50 flex-1 md:flex-none">
                <CardBody className="p-3 md:p-3">
                  <div className="flex items-center text-sm">
                    <div className="text-blue-200">
                      <p className="font-medium text-sm md:text-base">
                        ${totalesCarrito.total.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                      </p>
                      <p className="text-xs text-blue-300">
                        {totalesCarrito.cantidadTotal} items
                      </p>
                    </div>
                  </div>
                </CardBody>
              </Card>
            )}
          </div>
        </div>

        {/* Navegación por pestañas */}
        <div className="mb-6">
          <Card className="bg-gray-800">
            <CardBody className="p-0">
              <div className="flex flex-col md:flex-row">
                {tabs.map(tab => {
                  const IconComponent = tab.icon;
                  const isActive = activeTab === tab.id;
                  
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex-1 p-4 text-left transition-colors border-b md:border-b-0 md:border-r border-gray-700 last:border-r-0 last:border-b-0 ${
                        isActive 
                          ? 'bg-blue-900/30 text-blue-300 border-blue-800' 
                          : 'hover:bg-gray-700 text-gray-300'
                      }`}
                    >
                      <div className="flex items-center mb-2">
                        <IconComponent className={`mr-3 ${isActive ? 'text-blue-400' : 'text-gray-400'}`} />
                        <span className="font-medium">{tab.label}</span>
                      </div>
                      <p className="text-xs text-gray-400">{tab.description}</p>
                    </button>
                  );
                })}
              </div>
            </CardBody>
          </Card>
        </div>


{renderActiveComponent()}

        {/* Estadísticas flotantes */}
        {activeTab === 'inventario' && carrito.length > 0 && (
          <div 
            className="fixed bottom-20 md:bottom-4 right-2 md:right-4 z-40 max-w-[200px] md:max-w-none cursor-pointer"
            onClick={() => setShowCarrito(true)}
          >
            <Card className="bg-blue-900 border-blue-700 shadow-lg hover:bg-blue-800 transition-colors">
              <CardBody className="p-3 md:p-4">
                <div className="flex items-center">
                  <FaShoppingCart className="text-blue-400 mr-2 md:mr-3 flex-shrink-0" />
                  <div className="text-sm min-w-0">
                    <p className="font-medium text-blue-200 truncate">
                      Carrito: {carrito.length} items
                    </p>
                    <p className="text-blue-300 truncate">
                      Total: ${totalesCarrito.total.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                    </p>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        )}
      </div>

      {/* Modales */}
      <CarritoRepuestosExternos
        isOpen={showCarrito}
        onClose={() => setShowCarrito(false)}
        carrito={carrito}
        onUpdateCarrito={handleUpdateCarrito}
        onProcederPago={handleProcederPago}
      />

      <PagoRepuestosExternos
        isOpen={showPago}
        onClose={() => setShowPago(false)}
        datosCarrito={datosCarrito}
        onPagoCompletado={handlePagoCompletado}
        onVolverCarrito={() => {
          setShowPago(false);
          setShowCarrito(true);
        }}
      />
    </div>
  );
};

export default RepuestosMarketplace;